import React, { useCallback, useEffect, useState } from "react";

import { adjustUserBalance, formatLei, getUserBalance, parseLeiToBani } from "../../../api/balanceService";
import ConfirmDialog from "../../Common/ConfirmDialog";
import useSocketEvent from "../../../hooks/useSocketEvent";
import { ButtonUI, InputUI, ReservationCardUI, StatusUI } from "../../UI";
import ProfileListMeta from "./ProfileListMeta";
import { useProfileAlert } from "./useProfileAlert";

const TYPE_LABELS = {
   TOPUP_CARD: "Alimentare cu cardul",
   TOPUP_MANUAL: "Alimentare manuală",
   CORRECTION: "Corectare",
   PURCHASE: "Cumpărare",
   REFUND: "Rambursare",
};

const MAX_LEI = 10000;

function formatDate(value) {
   if (!value) return "";
   const d = new Date(value);
   const p = (n) => String(n).padStart(2, "0");
   return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Balanța elevului în profil (ADMIN, MANAGER văd; doar ADMIN alimentează / corectează).
 * Fiecare operație e un rând nou — nimic nu se modifică sau se șterge.
 */
export default function StudentFinanceTab({ studentId, canAdjust = false, isGuest = false }) {
   const [data, setData] = useState(null);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState("");
   const [amount, setAmount] = useState("");
   const [type, setType] = useState("TOPUP_MANUAL");
   const [reason, setReason] = useState("");
   const [saving, setSaving] = useState(false);
   const [formError, setFormError] = useState("");
   const [saved, setSaved] = useState("");
   const [pending, setPending] = useState(null); // operația care așteaptă confirmarea

   const load = useCallback(
      async (beforeId) => {
         if (!studentId) return;
         setLoading(true);
         setError("");
         try {
            const res = await getUserBalance(studentId, { beforeId, limit: 20 });
            setData((prev) => (beforeId && prev ? { ...res, entries: [...prev.entries, ...res.entries] } : res));
         } catch (e) {
            setError(e.message);
         } finally {
            setLoading(false);
         }
      },
      [studentId],
   );

   useEffect(() => {
      load();
   }, [load]);

   // soldul s-a schimbat (alt admin, alt dispozitiv, mai târziu o plată): fișa se reîmprospătează singură
   useSocketEvent(
      "balance:update",
      (payload) => {
         if (Number(payload?.userId) === Number(studentId)) load();
      },
      !!studentId && !isGuest,
   );

   const submit = async (e) => {
      e.preventDefault();
      setFormError("");
      setSaved("");
      const bani = parseLeiToBani(amount);
      if (bani === null || bani === 0) return setFormError("Scrie suma în lei, de exemplu 150 sau 150,50.");
      if (Math.abs(bani) > MAX_LEI * 100) return setFormError(`Cel mult ${MAX_LEI.toLocaleString("ro-RO")} lei într-o operație.`);
      if (type === "TOPUP_MANUAL" && bani < 0) return setFormError("Alimentarea e doar cu sumă pozitivă. Pentru scădere alege „Corectare”.");
      if (reason.trim().length < 3) return setFormError("Scrie motivul (cel puțin 3 caractere).");
      const current = Number(data?.balanceBani) || 0;
      if (current + bani < 0) return setFormError(`Soldul nu poate scădea sub zero (acum: ${formatLei(current)}).`);
      setPending({ bani, type, reason: reason.trim(), before: current });
   };

   const confirmSave = async () => {
      if (!pending) return;
      setSaving(true);
      try {
         const res = await adjustUserBalance(studentId, { amountBani: pending.bani, type: pending.type, reason: pending.reason });
         setSaved(`Salvat. Sold nou: ${formatLei(res.balanceBani)}`);
         setAmount("");
         setReason("");
         setPending(null);
         await load();
      } catch (err) {
         setPending(null);
         setFormError(err.message);
      } finally {
         setSaving(false);
      }
   };

   // erorile și confirmarea: în bula de sus a profilului (AlertPills), nu sub formular
   useProfileAlert(error);
   useProfileAlert(formError);
   useProfileAlert(saved, "success");

   // oaspeții n-au balanță (fila nici nu apare la ei); rămâne doar o stare goală, dacă se ajunge aici
   if (isGuest) {
      return <StatusUI tone="empty">Oaspeții nu au balanță. Primind o grupă, devin elevi obișnuiți.</StatusUI>;
   }

   const entries = data?.entries || [];

   // Din tiparele aplicației: soldul și formularul într-un InputUI.Group (ca fila Profil), mesajele
   // StatusUI, butoanele ButtonUI, operațiile casete lipite (ReservationCardUI, ca rezervările) cu
   // rândul comun de deasupra listelor (ProfileListMeta)
   return (
      <div className="studentsProfileUI__form">
         {canAdjust ? (
            <form className="studentsProfileUI__form" onSubmit={submit}>
               <InputUI.Group columns={2}>
                  <InputUI as="text" label="Sold" value={data ? formatLei(data.balanceBani) : "…"} />
                  <InputUI
                     as="select"
                     label="Operația"
                     value={type}
                     onChange={(e) => setType(e.target.value)}
                     disabled={saving}
                     options={[
                        { value: "TOPUP_MANUAL", label: "Alimentare manuală (ex.: numerar)" },
                        { value: "CORRECTION", label: "Corectare (+ sau −)" },
                     ]}
                  />
                  <InputUI
                     label="Suma"
                     inputMode="decimal"
                     placeholder={type === "CORRECTION" ? "Suma, ex.: -50" : "Suma în lei, ex.: 150"}
                     value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     disabled={saving}
                  />
                  <InputUI
                     label="Motivul"
                     placeholder="Obligatoriu, ex.: numerar la casă, chitanța 123"
                     value={reason}
                     maxLength={300}
                     onChange={(e) => setReason(e.target.value)}
                     disabled={saving}
                  />
               </InputUI.Group>
               <ButtonUI.Row>
                  <ButtonUI type="submit" variant="primary" disabled={saving} loading={saving}>
                     {saving ? "Se salvează…" : "Salvează"}
                  </ButtonUI>
               </ButtonUI.Row>
            </form>
         ) : (
            <InputUI.Group columns={1}>
               <InputUI as="text" label="Sold" value={data ? formatLei(data.balanceBani) : "…"} />
            </InputUI.Group>
         )}

         <ConfirmDialog
            open={!!pending}
            title={pending?.type === "TOPUP_MANUAL" ? "Confirmi alimentarea?" : "Confirmi corectarea?"}
            rows={
               pending
                  ? [
                       ["Operația", TYPE_LABELS[pending.type] || pending.type],
                       ["Suma", `${pending.bani > 0 ? "+" : ""}${formatLei(pending.bani)}`],
                       ["Sold acum", formatLei(pending.before)],
                       ["Sold după", formatLei(pending.before + pending.bani)],
                       ["Motiv", pending.reason],
                    ]
                  : []
            }
            note="Operația rămâne în istoric și în jurnal: nu se șterge, se poate doar corecta cu o operație nouă."
            confirmLabel={pending?.bani < 0 ? "Scade din sold" : "Alimentează"}
            tone={pending?.bani < 0 ? "danger" : "primary"}
            busy={saving}
            onConfirm={confirmSave}
            onCancel={() => setPending(null)}
         />

         <ProfileListMeta count={entries.length} one="operație" many="operații" />
         {data && entries.length === 0 ? <StatusUI tone="empty">Nicio operație încă.</StatusUI> : null}
         {entries.length ? (
            <ReservationCardUI.List>
               {entries.map((e) => (
                  <ReservationCardUI
                     key={e.id}
                     tone={e.amountBani < 0 ? "danger" : "success"}
                     date={`${e.amountBani > 0 ? "+" : ""}${formatLei(e.amountBani)}`}
                     lines={[
                        TYPE_LABELS[e.type] || e.type,
                        `${formatDate(e.createdAt)} · sold după: ${formatLei(e.balanceAfterBani)}${e.createdBy?.name ? ` · ${e.createdBy.name}` : ""}`,
                        e.reason || null,
                     ]}
                  />
               ))}
            </ReservationCardUI.List>
         ) : null}
         {data?.nextBeforeId ? (
            <ButtonUI.Row align="center">
               <ButtonUI disabled={loading} loading={loading} onClick={() => load(data.nextBeforeId)}>
                  Mai multe
               </ButtonUI>
            </ButtonUI.Row>
         ) : null}
         {loading && !data ? <StatusUI tone="loading" /> : null}
      </div>
   );
}
